import { guards } from '@shared/helpers/typeguards.helpers';
import {
  ElementValidationError,
  GenericValidationError,
  PropertyValidationError,
  StoreyValidationError,
  ValidationError,
  ValidationFn,
  ValidationResult,
} from './validation.models';
import { Extras } from '@sentry/core';
import { omitUndefined } from '@shared/helpers/object.helpers';

/**
 * Log a validation error as a group in the console.
 * @param result - The validation result to log
 * @param logOtherErrors - When `true`, also logs non-validation values via {@link console.error}. Default is `true`.
 * @returns The validation error if it is a validation error, otherwise undefined
 */
export const logValidationError = (
  result: unknown,
  logOtherErrors: boolean = true,
): ValidationError | undefined => {
  if (isValidationError(result)) {
    const message = result.message;

    // Verbose console logging with structured context

    console.group(`${message}`);
    // console.error?.(message);
    // console.error?.('Validation context:', result);
    if (result.stack) {
      console.error('Stack:\n', (result as any).stack);
    }
    if ('element' in result && result.element) {
      console.error('Element:', result.element);
    }
    if ('property' in result && result.property) {
      console.error('Property:', result.property);
    }
    if ('storey' in result && result.storey) {
      console.error('Storey:', result.storey);
    }

    console.groupEnd();
    return result;
  }

  // Log other errors if enabled
  if (logOtherErrors && guards.is.error(result)) {
    console.error(result);
  }
};

/**
 * Get additional context from a validation error to pass to Sentry.
 * @param result - The validation result to get the sentry extras from
 * @returns The sentry extras
 */
export const getSentryExtrasFromValidationError = (result: unknown): Extras => {
  if (isValidationError(result)) {
    return omitUndefined({
      element: 'element' in result && result.element,
      property: 'property' in result && result.property,
      storey: 'storey' in result && result.storey,
    });
  }
  return {};
};

export function getValidationErrorMsg(res: Error): string;
export function getValidationErrorMsg(
  res: ValidationResult,
): string | undefined;
export function getValidationErrorMsg(
  res: ValidationResult | Error,
): string | undefined {
  return guards.is.error(res) ? res.message : undefined;
}
/**
 * Helper function to throw validation errors. Pass a function that returns a string or boolean, or a string or boolean directly.
 * If the result is a string, it will be thrown as an error. If the result is false, a generic error message will be thrown.
 * @param fnOrResult - Function that returns true if valid, or a string or boolean if invalid
 * @param prefix - Prefix for the error message
 * @param onError - Optional callback to add additional logging/debugging
 */
export const throwValidationErrors = (
  fnOrResult: ValidationFn | ValidationResult,
  prefix = 'Validation error',
  onError?: (error: string) => void,
): void => {
  const result = typeof fnOrResult === 'function' ? fnOrResult() : fnOrResult;

  // Valid => do nothing
  if (result === true) {
    return;
  }
  const error = !guards.is.error(result)
    ? new Error('Unspecified validation error: ' + String(result))
    : result;

  // Apply prefix to error message (if missing)
  if (!error.message.startsWith(prefix)) {
    error.message = `${prefix}: ${error.message}`;
  }
  const message = error.message;
  const fullMessage = `${prefix}: ${message}`;

  onError?.(fullMessage);

  // AI-written and slight unsure if this is good or not?!
  // Attach structured error for consumers that catch and inspect.
  // Use Error options.cause when available; also mirror on a details field for wider runtime support.
  // const err: any = new Error(fullMessage, { cause: result });
  // if (result && typeof result === 'object') err.details = result;
  // // Prefer showing the origin (where the validation error was created) at the top of the stack
  // if (result && typeof result === 'object' && (result as any).stack) {
  //   const originLines = String((result as any).stack).split('\n');
  //   const originBody = originLines.slice(1).join('\n');
  //   err.stack = `Error: ${fullMessage}\n${originBody}`;
  // }
  throw error;
};

export const isValidationError = (result: unknown): result is ValidationError =>
  result instanceof GenericValidationError ||
  result instanceof ElementValidationError ||
  result instanceof PropertyValidationError ||
  result instanceof StoreyValidationError;
